import { Link, Navigate, useLocation } from "react-router-dom";
import { Lock, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUser } from "@/context/UserContext";
import { ProfileForm } from "@/components/ProfileSetup";

const Card = ({ children, testid }) => (
  <div className="max-w-xl mx-auto px-4 py-16" data-testid={testid}>
    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">{children}</div>
  </div>
);

export const SignedOutNotice = () => (
  <Card testid="gate-signed-out">
    <Lock className="text-blue-600" />
    <h1 className="font-heading text-2xl font-bold mt-4">Area riservata agli iscritti</h1>
    <p className="text-slate-600 mt-2">Per vedere i veicoli devi registrarti. Ogni iscrizione viene verificata e approvata dal nostro team.</p>
    <div className="flex gap-3 mt-6">
      <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="gate-register-button"><Link to="/registrati">Registrati</Link></Button>
      <Button asChild variant="outline" className="rounded-full" data-testid="gate-login-button"><Link to="/accedi">Accedi</Link></Button>
    </div>
  </Card>
);

export const RegistrationStep = () => {
  const { me } = useUser();
  return (
    <Card testid="gate-registration">
      {me.status === "rejected" && (
        <div className="flex gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 mb-6" data-testid="gate-rejected-notice">
          <XCircle className="text-rose-600 shrink-0" />
          <div>
            <p className="font-semibold text-rose-800">Registrazione non approvata</p>
            <p className="text-sm text-rose-700">{me.review_note || "Controlla i dati e i documenti, poi invia di nuovo la richiesta."}</p>
          </div>
        </div>
      )}
      <h1 className="font-heading text-2xl font-bold">Completa la registrazione</h1>
      <p className="text-slate-600 mt-1 mb-6">Inserisci i tuoi dati e carica i documenti. L'amministratore verificherà la tua richiesta.</p>
      <ProfileForm />
    </Card>
  );
};

export const PendingNotice = () => (
  <Card testid="gate-pending">
    <Clock className="text-amber-600" />
    <h1 className="font-heading text-2xl font-bold mt-4">Registrazione in attesa di approvazione</h1>
    <p className="text-slate-600 mt-2">Abbiamo ricevuto i tuoi dati e documenti. Potrai vedere i veicoli non appena l'amministratore avrà approvato la tua iscrizione.</p>
  </Card>
);

export const needsRegistration = (me) => !!me && me.role !== "admin" && me.status !== "approved" && me.status !== "pending";

export const RegistrationRedirect = () => {
  const { me } = useUser();
  const { pathname } = useLocation();
  if (!needsRegistration(me) || pathname === "/completa-registrazione") return null;
  return <Navigate to="/completa-registrazione" replace />;
};

export const Gate = ({ children }) => {
  const { me, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignedOutNotice />;
  if (!me) return <div className="py-24 text-center text-slate-500">Caricamento...</div>;
  if (me.status === "approved") return children;
  if (me.status === "pending") return <PendingNotice />;
  return <RegistrationStep />;
};
